import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  CheckCircle2,
  XCircle,
  FileCheck,
  AlertTriangle,
  Loader2,
  Camera,
  Gauge,
  Sparkles,
  Info,
} from "lucide-react";
import { trpcClient } from "@/lib/trpc";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

export interface ProofApprovalModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  plate: {
    id: string;
    plateId?: string;
    name?: string;
    productName?: string;
    customerName?: string;
    colors?: any;
    colorNames?: string[];
    size?: string;
    proofStatus?: string;
    proofApprover?: string;
    proofNotes?: string;
    proofSampleUrl?: string;
  } | null;
  onSuccess?: (result?: any) => void;
}

export default function ProofApprovalModal({
  open,
  onOpenChange,
  plate,
  onSuccess,
}: ProofApprovalModalProps) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<"APPROVED" | "REJECTED" | "BYPASSED">("APPROVED");
  const [approver, setApprover] = useState("");
  const [deltaE, setDeltaE] = useState<number | undefined>(1.2);
  const [targetDensity, setTargetDensity] = useState("C: 1.45, M: 1.50, Y: 1.05, K: 1.80");
  const [sampleUrl, setSampleUrl] = useState("");
  const [notes, setNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open && plate) {
      setStatus(plate.proofStatus === "REJECTED" ? "REJECTED" : "APPROVED");
      setApprover(plate.proofApprover || "KCS Chế Bản CTP");
      setDeltaE(1.2);
      setTargetDensity("C: 1.45, M: 1.50, Y: 1.05, K: 1.80");
      setSampleUrl(plate.proofSampleUrl || "");
      setNotes(
        plate.proofNotes ||
          "Màu sắc tờ in proof đã đối chiếu mẫu chuẩn Pantone & ISO 12647-2, tram mịn đồng đều."
      );
    }
  }, [open, plate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!plate) return;

    if (!approver.trim()) {
      toast.error("Vui lòng nhập tên người ký duyệt mẫu Proof");
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await (trpcClient as any).technical.plates.approveProof.mutate({
        id: plate.id,
        status,
        approver: approver.trim(),
        deltaE: deltaE !== undefined ? Number(deltaE) : undefined,
        targetDensity: targetDensity.trim() || undefined,
        sampleUrl: sampleUrl.trim() || undefined,
        notes: notes.trim() || undefined,
      });

      toast.success(
        status === "APPROVED"
          ? "Đã ký duyệt ĐẠT mẫu Proof bài in kẽm CTP"
          : status === "BYPASSED"
          ? "Đã kích hoạt Miễn Duyệt Proof cho bài in gấp"
          : "Đã từ chối mẫu Proof, yêu cầu xử lý lại file thiết kế"
      );

      await queryClient.invalidateQueries({ queryKey: ["plate", plate.id] });
      await queryClient.invalidateQueries({ queryKey: ["plates"] });

      onSuccess?.(result);
      onOpenChange(false);
    } catch (err: any) {
      toast.error(err.message || "Lỗi khi xử lý ký duyệt mẫu Proof");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!plate) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="p-2 bg-indigo-50 text-indigo-600 rounded-lg">
              <FileCheck className="w-5 h-5" />
            </div>
            <div>
              <DialogTitle className="text-lg font-bold text-slate-900">
                Ký Duyệt Mẫu In Thử Màu (Prepress Proof Approval)
              </DialogTitle>
              <DialogDescription className="text-xs text-slate-500">
                Chốt chặn kiểm soát chất lượng màu sắc trước khi xuất kẽm CTP lên máy in Offset
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Thông tin bài kẽm */}
          <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 text-xs grid grid-cols-2 gap-3">
            <div>
              <span className="text-slate-500">Mã bài kẽm:</span>{" "}
              <span className="font-mono font-bold text-slate-800">{plate.plateId || plate.id}</span>
            </div>
            <div>
              <span className="text-slate-500">Khách hàng:</span>{" "}
              <span className="font-medium text-slate-800">{plate.customerName || "—"}</span>
            </div>
            <div className="col-span-2">
              <span className="text-slate-500">Tên bài in / Sản phẩm:</span>{" "}
              <span className="font-semibold text-slate-900">
                {plate.productName || plate.name || "Bản kẽm Offset"}
              </span>
            </div>
            {plate.size && (
              <div>
                <span className="text-slate-500">Khổ kẽm:</span>{" "}
                <span className="font-mono text-slate-700">{plate.size}</span>
              </div>
            )}
            <div>
              <span className="text-slate-500">Trạng thái Proof hiện tại:</span>{" "}
              <Badge
                variant="outline"
                className={
                  plate.proofStatus === "APPROVED"
                    ? "bg-emerald-50 text-emerald-700 border-emerald-300"
                    : plate.proofStatus === "REJECTED"
                    ? "bg-rose-50 text-rose-700 border-rose-300"
                    : "bg-amber-50 text-amber-700 border-amber-300"
                }
              >
                {plate.proofStatus === "APPROVED"
                  ? "Đã Duyệt"
                  : plate.proofStatus === "REJECTED"
                  ? "Bị Từ Chối"
                  : "Chờ Duyệt Mẫu"}
              </Badge>
            </div>
          </div>

          {/* Quyết định duyệt */}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-700">
              Kết Quả Nghiệm Thu Mẫu Proof <span className="text-rose-500">*</span>
            </Label>
            <div className="grid grid-cols-3 gap-2">
              <button
                type="button"
                onClick={() => setStatus("APPROVED")}
                className={`flex items-center justify-center gap-2 p-2.5 rounded-lg border text-xs font-semibold transition-all ${
                  status === "APPROVED"
                    ? "bg-emerald-50 border-emerald-500 text-emerald-800 ring-2 ring-emerald-200"
                    : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                }`}
              >
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                <span>✅ Ký Duyệt Đạt</span>
              </button>

              <button
                type="button"
                onClick={() => setStatus("REJECTED")}
                className={`flex items-center justify-center gap-2 p-2.5 rounded-lg border text-xs font-semibold transition-all ${
                  status === "REJECTED"
                    ? "bg-rose-50 border-rose-500 text-rose-800 ring-2 ring-rose-200"
                    : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                }`}
              >
                <XCircle className="w-4 h-4 text-rose-600" />
                <span>❌ Từ Chối Sửa File</span>
              </button>

              <button
                type="button"
                onClick={() => setStatus("BYPASSED")}
                className={`flex items-center justify-center gap-2 p-2.5 rounded-lg border text-xs font-semibold transition-all ${
                  status === "BYPASSED"
                    ? "bg-amber-50 border-amber-500 text-amber-800 ring-2 ring-amber-200"
                    : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                }`}
              >
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                <span>⚠️ Miễn Duyệt (Gấp)</span>
              </button>
            </div>
          </div>

          {/* Form nhập liệu thông số */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs font-medium text-slate-700">
                Người Ký Duyệt <span className="text-rose-500">*</span>
              </Label>
              <Input
                placeholder="VD: Nguyễn Văn A (Prepress Lead / QC)"
                value={approver}
                onChange={(e) => setApprover(e.target.value)}
                className="h-8 text-xs"
                required
              />
            </div>

            <div className="space-y-1">
              <Label className="text-xs font-medium text-slate-700 flex items-center gap-1">
                <Gauge className="w-3.5 h-3.5 text-slate-400" />
                Sai Lệch Màu (Delta E &le; 2.0)
              </Label>
              <Input
                type="number"
                step="0.1"
                min="0"
                max="20"
                placeholder="VD: 1.2"
                value={deltaE ?? ""}
                onChange={(e) => setDeltaE(e.target.value ? Number(e.target.value) : undefined)}
                className="h-8 text-xs font-mono"
              />
            </div>

            <div className="col-span-2 space-y-1">
              <Label className="text-xs font-medium text-slate-700 flex items-center gap-1">
                <Sparkles className="w-3.5 h-3.5 text-slate-400" />
                Mật Độ Tram Mục Tiêu (Target Density CMYK)
              </Label>
              <Input
                placeholder="VD: C: 1.45, M: 1.50, Y: 1.05, K: 1.80"
                value={targetDensity}
                onChange={(e) => setTargetDensity(e.target.value)}
                className="h-8 text-xs font-mono"
              />
            </div>

            <div className="col-span-2 space-y-1">
              <Label className="text-xs font-medium text-slate-700 flex items-center gap-1">
                <Camera className="w-3.5 h-3.5 text-slate-400" />
                Link Ảnh Chụp Mẫu In Proof Có Ký Tên
              </Label>
              <Input
                placeholder="https://... ảnh tờ in proof test hoặc file PDF kiểm màu"
                value={sampleUrl}
                onChange={(e) => setSampleUrl(e.target.value)}
                className="h-8 text-xs"
              />
            </div>

            <div className="col-span-2 space-y-1">
              <Label className="text-xs font-medium text-slate-700">
                Ghi Chú Đánh Giá Kỹ Thuật
              </Label>
              <Textarea
                rows={2}
                placeholder="Nhận xét độ mịn tram, màu pha Pantone, yêu cầu canh mực trên máy in..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="text-xs resize-none"
              />
            </div>
          </div>

          <DialogFooter className="pt-2 border-t border-slate-100 flex items-center justify-between sm:justify-between">
            <div className="flex items-center gap-1.5 text-slate-400 text-xs">
              <Info className="w-3.5 h-3.5" />
              <span>Duyệt Đạt sẽ mở khóa trạng thái Sẵn Sàng Ghi Bản Kẽm</span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Hủy
              </Button>
              <Button
                type="submit"
                size="sm"
                className={
                  status === "APPROVED"
                    ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                    : status === "REJECTED"
                    ? "bg-rose-600 hover:bg-rose-700 text-white"
                    : "bg-amber-600 hover:bg-amber-700 text-white"
                }
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                    Đang lưu...
                  </>
                ) : status === "APPROVED" ? (
                  "✅ Xác Nhận Ký Duyệt"
                ) : status === "REJECTED" ? (
                  "❌ Xác Nhận Từ Chối"
                ) : (
                  "⚠️ Xác Nhận Miễn Duyệt"
                )}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
